import React, { useState, useEffect, useContext } from "react";
import { Link } from "react-router-dom";
import { ApplicationContext } from "./ApplicationContext";
import InfluencerBudgetService from "../services/InfluencerBudgetService";
import IndentService from "../services/IndentService";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import { toastService } from '../services/toastService';
import { formatCurrency, getUtilizationColor } from './utils';

// Loading Spinner Component
const LoadingSpinner = () => (
  <div className="flex justify-center items-center min-h-[400px]">
    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500"></div>
  </div>
);

// Stat Card Component
const StatCard = ({ title, value, subtitle, icon, color, highlight }) => {
  const colorClasses = {
    blue: { bg: 'bg-blue-50', icon: 'bg-blue-100 text-blue-600', border: 'border-blue-200' },
    orange: { bg: 'bg-orange-50', icon: 'bg-orange-100 text-orange-600', border: 'border-orange-200' },
    purple: { bg: 'bg-purple-50', icon: 'bg-purple-100 text-purple-600', border: 'border-purple-200' },
    green: { bg: 'bg-green-50', icon: 'bg-green-100 text-green-600', border: 'border-green-200' },
  };

  const colors = colorClasses[color] || colorClasses.purple;

  const icons = {
    wallet: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
      </svg>
    ),
    cart: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    cash: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    ),
    rollover: (
      <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
      </svg>
    ),
  };

  return (
    <div className={`rounded-xl border p-6 transition-all duration-200 hover:shadow-md ${highlight ? 'bg-purple-50 border-purple-300 shadow-sm' : 'bg-white border-gray-200'}`}>
      <div className="flex items-center justify-between">
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-gray-500 mb-1">{title}</p>
          <p className={`text-2xl font-bold truncate ${highlight ? 'text-purple-700' : 'text-gray-900'}`}>
            {value}
          </p>
          {subtitle && (
            <p className="text-xs text-gray-400 mt-1">{subtitle}</p>
          )}
        </div>
        <div className={`w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 ml-4 ${colors.icon}`}>
          {icons[icon]}
        </div>
      </div>
    </div>
  );
};

// Budget Progress Bar Component
const BudgetProgressBar = ({ percentage, spent, allocated }) => {
  const getTextColor = () => {
    if (percentage < 50) return 'text-green-600';
    if (percentage < 80) return 'text-yellow-600';
    return 'text-red-600';
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex justify-between items-center mb-3">
        <span className="text-sm font-medium text-gray-600">Iskorišćenost budžeta</span>
        <span className={`text-lg font-bold ${getTextColor()}`}>{percentage.toFixed(1)}%</span>
      </div>
      <div className="w-full bg-gray-200 rounded-full h-4 overflow-hidden">
        <div
          className={`${getUtilizationColor(percentage)} h-4 rounded-full transition-all duration-700 ease-out`}
          style={{ width: `${Math.min(percentage, 100)}%` }}
        />
      </div>
      <div className="flex justify-between mt-2 text-xs text-gray-500">
        <span>0 RSD</span>
        <span>{allocated} RSD</span>
      </div>
    </div>
  );
};

// Spending Chart Component
const SpendingChart = ({ data }) => {
  if (!data || data.length === 0) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Mesečna Potrošnja</h3>
        <div className="flex items-center justify-center h-[300px] text-gray-400">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p>Nema podataka za prikaz</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <h3 className="text-lg font-semibold text-gray-900 mb-4">Mesečna Potrošnja</h3>
      <ResponsiveContainer width="100%" height={300}>
        <BarChart data={data} margin={{ top: 20, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
          />
          <YAxis
            tick={{ fontSize: 12, fill: '#6b7280' }}
            axisLine={{ stroke: '#e5e7eb' }}
            tickFormatter={(v) => `${(v/1000).toFixed(0)}k`}
          />
          <Tooltip
            formatter={(value) => [`${Number(value).toLocaleString('sr-RS', { minimumFractionDigits: 2 })} RSD`]}
            contentStyle={{
              borderRadius: '8px',
              border: '1px solid #e5e7eb',
              boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
            }}
            labelStyle={{ fontWeight: 'bold', marginBottom: '4px' }}
          />
          <Legend
            wrapperStyle={{ paddingTop: '16px' }}
          />
          <Bar dataKey="Dodeljeno" fill="#c4b5fd" radius={[4, 4, 0, 0]} name="Dodeljeno" />
          <Bar dataKey="Potrošeno" fill="#7c3aed" radius={[4, 4, 0, 0]} name="Potrošeno" />
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    PENDING: { label: 'Na čekanju', bg: 'bg-yellow-100', text: 'text-yellow-800' },
    ACTIVATED: { label: 'Aktivirana', bg: 'bg-blue-100', text: 'text-blue-800' },
    DELIVERED: { label: 'Isporučena', bg: 'bg-green-100', text: 'text-green-800' },
    CANCELLED: { label: 'Otkazana', bg: 'bg-red-100', text: 'text-red-800' },
  };

  const config = statusConfig[status] || { label: status, bg: 'bg-gray-100', text: 'text-gray-800' };

  return (
    <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.bg} ${config.text}`}>
      {config.label}
    </span>
  );
};

// Recent Orders Table Component
const RecentOrdersTable = ({ orders, formatDate }) => {
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4">
        <h3 className="text-lg font-semibold text-gray-900">Poslednje Porudžbine</h3>
        <Link
          to="/indents"
          className="text-sm text-purple-600 hover:text-purple-800 font-medium"
        >
          Vidi sve
        </Link>
      </div>

      {orders.length === 0 ? (
        <div className="flex items-center justify-center h-[250px] text-gray-400">
          <div className="text-center">
            <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
            <p>Nema porudžbina</p>
          </div>
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="pb-3 font-medium">Šifra</th>
                <th className="pb-3 font-medium">Datum</th>
                <th className="pb-3 font-medium text-right">Iznos</th>
                <th className="pb-3 font-medium">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {orders.map((order, i) => (
                <tr key={order.code || i} className="hover:bg-gray-50">
                  <td className="py-3">
                    <Link
                      to={`/indents/entries/${order.code}`}
                      className="font-medium text-purple-600 hover:text-purple-800"
                    >
                      {order.code}
                    </Link>
                  </td>
                  <td className="py-3 text-gray-500">{formatDate(order.creationTime)}</td>
                  <td className="py-3 text-right font-medium">{formatCurrency(order.bill)} RSD</td>
                  <td className="py-3">
                    <StatusBadge status={order.indentStatus} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

// Main Dashboard Component
const InfluencerDashboard = () => {
  const [currentBudget, setCurrentBudget] = useState(null);
  const [budgetHistory, setBudgetHistory] = useState([]);
  const [recentOrders, setRecentOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  const { loggedInClient } = useContext(ApplicationContext);

  // Format date helper
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('sr-RS', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      });
    } catch (e) {
      return dateString;
    }
  };

  // Format month for chart display
  const formatMonth = (yearMonth) => {
    if (!yearMonth) return '';
    const [year, month] = yearMonth.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Maj', 'Jun', 'Jul', 'Avg', 'Sep', 'Okt', 'Nov', 'Dec'];
    return `${monthNames[parseInt(month) - 1]} ${year.slice(2)}`;
  };

  // Load dashboard data
  useEffect(() => {
    const loadDashboardData = async () => {
      try {
        setLoading(true);

        // Parallel API calls for current budget and orders
        const [budgetRes, ordersRes] = await Promise.all([
          InfluencerBudgetService.getMyBudget().catch(() => ({ data: null })),
          IndentService.getAll().catch(() => ({ data: [] }))
        ]);

        setCurrentBudget(budgetRes.data);

        // Sort orders by date and take last 5
        const sortedOrders = (ordersRes.data || [])
          .sort((a, b) => new Date(b.creationTime) - new Date(a.creationTime))
          .slice(0, 5);
        setRecentOrders(sortedOrders);

        // Fetch budget history for the authenticated influencer user
        try {
          const historyRes = await InfluencerBudgetService.getMyBudgetHistory();
          setBudgetHistory(historyRes.data || []);
        } catch (error) {
          console.log('No budget history available');
        }
      } catch (error) {
        toastService.error('Greška pri učitavanju podataka');
      } finally {
        setLoading(false);
      }
    };

    loadDashboardData();
  }, [loggedInClient]);

  // Calculate spent percentage
  const spentPercentage = currentBudget && currentBudget.allocatedAmount > 0
    ? Math.min(100, (currentBudget.spentAmount / currentBudget.allocatedAmount) * 100)
    : 0;

  // Transform history for chart (last 6 months)
  const chartData = budgetHistory
    .map(b => ({
      name: formatMonth(b.yearMonth),
      Dodeljeno: b.allocatedAmount || 0,
      Potrošeno: b.spentAmount || 0
    }))
    .reverse()
    .slice(-6);

  // Get current month/year for display
  const currentMonth = new Date().toLocaleDateString('sr-RS', { month: 'long', year: 'numeric' });

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <div className="container mx-auto px-4 py-6 max-w-7xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Moj Dashboard</h1>
            <p className="text-gray-500 mt-1">Pregled budžeta i potrošnje za {currentMonth}</p>
          </div>
          <Link
            to="/shop"
            className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-medium"
          >
            <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2.293 2.293c-.63.63-.184 1.707.707 1.707H17m0 0a2 2 0 100 4 2 2 0 000-4zm-8 2a2 2 0 11-4 0 2 2 0 014 0z" />
            </svg>
            Idi u Shop
          </Link>
        </div>
      </div>

      {/* No budget message */}
      {!currentBudget && (
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6 mb-8">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <div>
              <h3 className="text-sm font-medium text-yellow-800">Budžet nije dodeljen</h3>
              <p className="text-sm text-yellow-700 mt-1">
                Budžet za ovaj mesec još uvek nije dodeljen. Kontaktirajte administratora za više informacija.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-8">
        <StatCard
          title="Dodeljeno"
          value={`${formatCurrency(currentBudget?.allocatedAmount || 0)} RSD`}
          subtitle="Ukupan mesečni budžet"
          icon="wallet"
          color="blue"
        />
        <StatCard
          title="Potrošeno"
          value={`${formatCurrency(currentBudget?.spentAmount || 0)} RSD`}
          subtitle="Ovog meseca"
          icon="cart"
          color="orange"
        />
        <StatCard
          title="Dostupno"
          value={`${formatCurrency(currentBudget?.availableBudget || 0)} RSD`}
          subtitle="Preostalo za trošenje"
          icon="cash"
          color="purple"
          highlight
        />
        <StatCard
          title="Preneto"
          value={`${formatCurrency(currentBudget?.rolloverAmount || 0)} RSD`}
          subtitle="Iz prethodnog meseca"
          icon="rollover"
          color="green"
        />
      </div>

      {/* Progress Bar */}
      {currentBudget && currentBudget.allocatedAmount > 0 && (
        <div className="mb-8">
          <BudgetProgressBar
            percentage={spentPercentage}
            spent={formatCurrency(currentBudget.spentAmount)}
            allocated={formatCurrency(currentBudget.allocatedAmount)}
          />
        </div>
      )}

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <SpendingChart data={chartData} />
        <RecentOrdersTable
          orders={recentOrders}
          formatDate={formatDate}
        />
      </div>
    </div>
  );
};

export default InfluencerDashboard;
